import { useEffect, useState } from "react";
import { MessageCircleQuestion, Users, Repeat, ThumbsUp, ThumbsDown, Download, TrendingUp, Sparkles } from "lucide-react";
import { api, fmtDateTime } from "@/lib/api";
import { Kpi, Panel, RankTable, Donut, TrendArea } from "@/lib/adminUI";
import { exportCsv } from "@/lib/exportCsv";
import { Skeleton } from "@/components/common";

const GROUP_LABEL = {
  NEXT_EMI_DATE: "Next EMI / Due date", OUTSTANDING_AMOUNT: "Outstanding amount",
  PAYMENT_STATUS: "Payment status", CARD_BILL: "Credit card bill", REWARDS: "Rewards & coins",
  LOAN_DETAILS: "Loan details", TRANSACTION: "Transactions & receipts", ACCOUNT: "Account & profile",
  SUPPORT: "Support & help", OTHER: "Other",
};
const label = (g) => GROUP_LABEL[g] || g;
const RANGES = [{ k: 7, l: "7 Days" }, { k: 30, l: "30 Days" }, { k: 90, l: "90 Days" }];

export default function AdminAskAIInsights() {
  const [d, setD] = useState(undefined);
  const [days, setDays] = useState(7);

  useEffect(() => {
    setD((p) => (p === null ? null : p));
    api.get(`/admin/askai/insights?days=${days}`).then((r) => setD(r.data)).catch(() => setD(null));
  }, [days]);

  if (d === undefined) return <div className="space-y-4"><Skeleton className="h-24 w-full" /><Skeleton className="h-64 w-full" /></div>;
  if (d === null) return <div className="rounded-xl border border-slate-200 bg-white p-10 text-center text-sm text-slate-500" data-testid="askai-insights-forbidden">You don't have access to Ask AI Insights.</div>;

  const notHelpfulPct = d.helpful + d.not_helpful > 0 ? 100 - d.helpful_pct : 0;

  const exportGroups = () => exportCsv(
    d.top_questions.map((t) => ({
      "Question Group": label(t.group), Count: t.count, "Unique Users": t.unique_users,
      "Repeat Asks": t.repeat_asks, "Last Asked": fmtDateTime(t.last_asked),
    })), "askai-question-groups");

  return (
    <div className="space-y-6" data-testid="askai-insights-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 font-display text-2xl font-bold text-[#0B132B]"><Sparkles size={22} className="text-[#14B8A6]" /> Ask AI Insights</h1>
          <p className="text-sm text-slate-500">What customers ask the assistant • answer content is never stored</p>
        </div>
        <button onClick={exportGroups} data-testid="askai-export" className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-[#0B132B] hover:border-[#14B8A6]">
          <Download size={15} /> Export CSV
        </button>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Kpi label="Total Questions" value={d.total} icon={MessageCircleQuestion} hint={`${d.today} today · ${d.last7} in 7d`} />
        <Kpi label="Unique Users" value={d.unique_users} icon={Users} hint={`${d.avg_per_user} avg / user`} accent="#6366F1" />
        <Kpi label="Repeat Asks" value={d.repeat_asks} icon={Repeat} hint={`${d.unique_groups} topic groups`} accent="#F59E0B" />
        <Kpi label="Helpful Rate" value={`${d.helpful_pct}%`} icon={ThumbsUp} hint={`${d.helpful} 👍 · ${d.not_helpful} 👎`} accent="#10B981" />
      </div>

      {/* Day-by-day trends (server-aggregated) */}
      <div>
        <div className="mb-3 flex items-center justify-end gap-1 rounded-lg border border-slate-200 bg-white p-1" style={{ width: "fit-content", marginLeft: "auto" }} data-testid="askai-range">
          {RANGES.map((r) => (
            <button key={r.k} data-testid={`askai-range-${r.k}`} onClick={() => setDays(r.k)}
              className={`rounded-md px-3 py-1 text-xs font-semibold transition-colors ${days === r.k ? "bg-[#0B132B] text-white" : "text-slate-500 hover:text-[#0B132B]"}`}>{r.l}</button>
          ))}
        </div>
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <Panel title="Daily question volume" subtitle={`Questions asked · last ${d.days} days`}>
            <TrendArea data={d.daily} dataKey="questions" money={false} height={240} />
          </Panel>
          <Panel title="Daily helpful rate" subtitle={`👍 helpful % · last ${d.days} days`}>
            <TrendArea data={d.daily} dataKey="helpful_pct" money={false} height={240} />
          </Panel>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <Panel title="Top question topics" subtitle="Grouped by intent (rule-based, no LLM cost)">
            <RankTable
              cols={["Topic", "Count", "Unique Users", "Repeat Asks", "Last Asked"]}
              rows={d.top_questions}
              render={(t) => (
                <tr key={t.group} data-testid={`askai-group-${t.group}`}>
                  <td className="px-4 py-3 font-medium text-[#0B132B]">{label(t.group)}</td>
                  <td className="px-4 py-3">{t.count}</td>
                  <td className="px-4 py-3">{t.unique_users}</td>
                  <td className="px-4 py-3">{t.repeat_asks}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-slate-500">{fmtDateTime(t.last_asked)}</td>
                </tr>
              )}
            />
            {d.top_questions.length === 0 && <p className="py-6 text-center text-sm text-slate-400">No questions logged yet.</p>}
          </Panel>
        </div>

        <div className="space-y-6">
          <Panel title="Answer feedback" subtitle="👍 helpful vs 👎 not helpful">
            {d.helpful + d.not_helpful > 0 ? (
              <Donut data={[{ name: "Helpful", value: d.helpful }, { name: "Not helpful", value: d.not_helpful }]} height={200} />
            ) : <p className="py-10 text-center text-sm text-slate-400">No feedback yet.</p>}
            <div className="mt-2 flex justify-around text-center">
              <div><p className="flex items-center justify-center gap-1 text-lg font-bold text-emerald-600"><ThumbsUp size={15} /> {d.helpful_pct}%</p><p className="text-xs text-slate-400">Helpful</p></div>
              <div><p className="flex items-center justify-center gap-1 text-lg font-bold text-red-500"><ThumbsDown size={15} /> {notHelpfulPct}%</p><p className="text-xs text-slate-400">Not helpful</p></div>
            </div>
          </Panel>

          <Panel title="Emerging topics" subtitle="Rising in the last 7 days">
            {d.emerging.length ? (
              <div className="space-y-2">
                {d.emerging.map((e) => (
                  <div key={e.group} className="flex items-center justify-between rounded-lg border border-slate-100 bg-slate-50 px-3 py-2" data-testid={`askai-emerging-${e.group}`}>
                    <span className="text-sm font-medium text-[#0B132B]">{label(e.group)}</span>
                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-600"><TrendingUp size={13} /> +{e.pct}% <span className="text-slate-400">({e.prev}→{e.count})</span></span>
                  </div>
                ))}
              </div>
            ) : <p className="py-6 text-center text-sm text-slate-400">No emerging topics.</p>}
          </Panel>
        </div>
      </div>
    </div>
  );
}
